import { useEffect, useMemo, useState } from "react";
import { Column } from "primereact/column";
import { DataTable, DataTableStateEvent, SortOrder } from "primereact/datatable";
import { useAppSelector } from "../../../app/hooks";
import { AvatarWithName } from "../../../components/Avatar";
import { ClassLinkOrCleverIcon } from "../../../components/ClassLinkOrCleverIcon";
import FormattedDateAndTime from "../../../components/FormattedDateAndTime";
import SearchFilter from "../../../components/input/SearchFilter";
import SimplePagination from "../../../components/SimplePagination";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { CourseUserViewModel } from "../coursesApiSlice";

interface SchoolAdminsTableProps {
	users: CourseUserViewModel[];
	displayLength: number;
	isTeacherTable: boolean;
}

export default function CourseUsersTable(props: SchoolAdminsTableProps) {
	const { config } = useAppSelector((state) => state.site);
	const hasClassLinkOrCleverUser = props.users?.some((u) => u.classLinkId || u.cleverId);

	const [page, setPage] = useState(0);
	const [searchFilter, setSearchFilter] = useState("");
	const [isFiltered, setIsFiltered] = useState(false);
	const [sortField, setSortField] = useState<string>();
	const [sortOrder, setSortOrder] = useState<SortOrder>(0);

	const filteredUsers =
		props.users.filter((user) => {
			if (!searchFilter) return true;
			const searchLower = searchFilter.toLowerCase();
			return (
				user.name?.toLowerCase().includes(searchLower) ||
				user.username?.toLowerCase().includes(searchLower) ||
				user.externalId?.toLowerCase().includes(searchLower)
			);
		}) ?? [];

	const sortedUsers = useMemo(() => {
		if (!sortField || sortOrder === 0 || !sortOrder) {
			return filteredUsers;
		}

		const sorted = [...filteredUsers].sort((a, b) => {
			let aValue: any;
			let bValue: any;

			if (sortField === "lastLoggedIn") {
				aValue = a.lastLoggedIn ? new Date(a.lastLoggedIn).getTime() : 0;
				bValue = b.lastLoggedIn ? new Date(b.lastLoggedIn).getTime() : 0;
			} else {
				aValue = (a[sortField as keyof CourseUserViewModel] as string)?.toLowerCase() || "";
				bValue = (b[sortField as keyof CourseUserViewModel] as string)?.toLowerCase() || "";
			}

			if (aValue < bValue) return -sortOrder;
			if (aValue > bValue) return sortOrder;
			return 0;
		});

		return sorted;
	}, [filteredUsers, sortField, sortOrder]);

	const onSort = (event: DataTableStateEvent) => {
		setSortField(event.sortField);
		setSortOrder(event.sortOrder ?? 0);
		setPage(0);
	};

	const paginatedUsers = sortedUsers.slice(
		page * props.displayLength,
		(page + 1) * props.displayLength
	);

	const handlePageChange = (newPage: number) => {
		setPage(newPage);
	};

	useEffect(() => {
		setIsFiltered(!!searchFilter);
		setPage(0);
	}, [searchFilter]);

	const nameBodyTemplate = (row: CourseUserViewModel) => {
		return (
			<AvatarWithName
				userId={Number(row.userId)}
				name={row.name}
				initials={row.initials}
				hasImage={row.hasImage}
			/>
		);
	};

	const cleverOrClassLinkBodyTemplate = (row: CourseUserViewModel) => {
		return (
			<ClassLinkOrCleverIcon
				isClassLinkEntity={!!row.classLinkId}
				isCleverEntity={!!row.cleverId}
			/>
		);
	};

	const lastLoggedInBodyTemplate = (row: CourseUserViewModel) => {
		return (
			<span>
				<FormattedDateAndTime date={row.lastLoggedIn} />
			</span>
		);
	};

	return (
		<section>
			<div className="flex gap-3 mt-4 items-end">
				<SearchFilter
					id={`${props.isTeacherTable ? "teacher" : "student"}Search`}
					name={`${props.isTeacherTable ? "teacher" : "student"}Search`}
					searchFilter={searchFilter}
					setSearchFilter={setSearchFilter}
					label="Search"
					placeHolder="Search by name, username or external ID..."
					className="w-96"
				/>
			</div>
			<DataTable
				value={paginatedUsers}
				dataKey="userId"
				emptyMessage={`No ${props.isTeacherTable ? "teachers" : "students"} found`}
				stripedRows
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ paddingTop: "16px" }}
				sortField={sortField}
				sortOrder={sortOrder}
				onSort={onSort}
				sortIcon={CustomSortIcon}>
				<Column header="Name" body={nameBodyTemplate} sortable sortField="name"></Column>
				{!config?.isUtahComposeSite && hasClassLinkOrCleverUser && (
					<Column
						body={cleverOrClassLinkBodyTemplate}
						headerClassName="!px-0 w-4"
						className="!px-0"></Column>
				)}
				<Column header="Username" field="username" sortable></Column>
				<Column header="External ID" field="externalId" sortable></Column>
				{props.isTeacherTable && <Column header="Email" field="email" sortable></Column>}
				<Column
					header="Last Login Date"
					body={lastLoggedInBodyTemplate}
					sortable
					sortField="lastLoggedIn"
				/>
			</DataTable>
			<SimplePagination
				itemCount={sortedUsers?.length}
				page={page}
				pageSize={props.displayLength}
				isFiltered={isFiltered}
				totalCount={props.users?.length}
				onChange={(page) => handlePageChange(page)}
				className={"border-none"}
			/>
		</section>
	);
}
